import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  signupUser as apiSignupUser,
  loginUser as apiLoginUser,
  getMyProfile,
} from "@services/httpservice";
import { parseError } from "../../utils/utils";

// Helper to get user info from localStorage
const getUserInfoFromStorage = () => {
  try {
    const userInfoString = localStorage.getItem('userInfo');
    const userInfo = userInfoString ? JSON.parse(userInfoString) : null;

    // Validate that userInfo has required fields
    if (userInfo && (!userInfo.role || !userInfo.email)) {
      localStorage.removeItem('userInfo');
      return null;
    }

    return userInfo;
  } catch (error) {
    console.error('Error parsing userInfo from localStorage', error);
    localStorage.removeItem('userInfo');
    return null;
  }
};

// Async thunk for user signup
export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async (userData, { rejectWithValue }) => {
    try {
      const data = await apiSignupUser(userData);
      if (data && data.token) {
        localStorage.setItem('accessToken', data.token);
        localStorage.setItem('userInfo', JSON.stringify(data.user || data));
      }
      return data;
    } catch (error) {
      return rejectWithValue(parseError(error));
    }
  }
);

// Async thunk for user login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (loginData, { rejectWithValue }) => {
    try {
      const tokenData = await apiLoginUser(loginData);

      if (tokenData && tokenData.access) {
        localStorage.setItem('accessToken', tokenData.access);
        if (tokenData.refresh) {
          localStorage.setItem('refreshToken', tokenData.refresh);
        }

        // Use the user data directly from the login response instead of making another API call
        if (tokenData.user) {
          localStorage.setItem('userInfo', JSON.stringify(tokenData.user));
          return tokenData.user;
        } else {
          // Fallback to getMyProfile if user data is not in login response
          const userProfile = await getMyProfile();
          localStorage.setItem('userInfo', JSON.stringify(userProfile));
          return userProfile;
        }
      } else {
        return rejectWithValue('Login failed: No access token received.');
      }
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      return rejectWithValue(parseError(error));
    }
  }
);

const initialState = {
  userInfo: getUserInfoFromStorage(),
  loading: false,
  error: null,
  signupMessage: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('userInfo');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      state.userInfo = null;
      state.loading = false;
      state.error = null;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    clearSignupMessage: (state) => {
      state.signupMessage = null;
    },
    updateUserInfo: (state, action) => {
      state.userInfo = {
        ...state.userInfo,
        ...action.payload,
      };
      localStorage.setItem('userInfo', JSON.stringify(state.userInfo)); // sync update to localStorage
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload.user;
        state.signupMessage = action.payload.message;
        state.error = null;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.userInfo = null;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.userInfo = null;
      });
  },
});

export const {
  logout,
  clearAuthError,
  clearSignupMessage,
  updateUserInfo,
} = authSlice.actions;

// Selectors
export const selectUserInfo = (state) => state.auth.userInfo;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;